import { byId } from '../domUtils';
import type { PrototypeRuntime, Root } from '../prototypeTypes';
import { getRecoverableTurnstileToken } from '../turnstileRuntime';
import { resolveAttachmentMime } from '../validation';
import { beginFeedbackAttachmentUpload, finishFeedbackAttachmentUpload, isCurrentFeedbackAttachmentUpload } from '../feedbackAttachmentUploadState';
import { clearOperationFeedback, paintOperationFeedback } from '../operationFeedback';

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
const MAX_BYTES = 5 * 1024 * 1024;

export async function handleFeedbackAttachmentChange(event: Event, root: Root, runtime: PrototypeRuntime): Promise<void> {
	const input = event.target as HTMLInputElement;
	const file = input.files?.[0];
	input.value = '';
	if (!file) return;
	if (!IMAGE_TYPES.has(resolveAttachmentMime(file) ?? '')) {
		paintOperationFeedback(root, 'feedbackAttachmentAlert', 'Unsupported file type. PNG, JPG, WEBP only.');
		return;
	}
	if (file.size <= 0 || file.size > MAX_BYTES) {
		paintOperationFeedback(root, 'feedbackAttachmentAlert', 'File too large. Max 5 MB.');
		return;
	}
	clearOperationFeedback(root, 'feedbackAttachmentAlert');
	const uploadGeneration = beginFeedbackAttachmentUpload(root);

	const preview = byId(root, 'feedbackFilePreview');
	const name = byId(root, 'feedbackFileName');
	const clear = byId(root, 'feedbackFileClear') as HTMLButtonElement | null;
	const zone = byId(root, 'feedbackUploadZone');
	if (preview) {
		preview.style.display = '';
		preview.style.opacity = '0.76';
	}
	if (name) name.textContent = `Uploading ${file.name}...`;
	if (clear) {
		clear.style.display = '';
		clear.setAttribute('aria-label', 'Cancel screenshot upload');
		clear.setAttribute('title', 'Cancel screenshot upload');
	}
	if (zone) zone.style.pointerEvents = 'none';
	if (preview) {
		preview.removeAttribute('data-uploaded-key');
		preview.removeAttribute('data-uploaded-submission-id');
	}

	try {
		const turnstileToken = await getRecoverableTurnstileToken(runtime, 'uploads-presign-feedback');
		const attachment = await runtime.api.uploadAttachment(file, 'feedback', crypto.randomUUID(), turnstileToken);
		if (!isCurrentFeedbackAttachmentUpload(root, uploadGeneration)) {
			void runtime.api.deleteAttachment(attachment.objectKey).catch((error) => console.warn('[feedback] failed to remove stale attachment', error));
			return;
		}
		if (preview) {
			preview.setAttribute('data-uploaded-key', attachment.objectKey);
			preview.setAttribute('data-uploaded-submission-id', attachment.submissionId);
			preview.style.opacity = '';
		}
		if (name) name.textContent = `${attachment.name} · ${Math.round(attachment.size / 1024)} KB`;
		if (clear) {
			clear.style.display = '';
			clear.setAttribute('aria-label', 'Remove screenshot');
			clear.setAttribute('title', 'Remove screenshot');
		}
		if (zone) zone.style.pointerEvents = '';
		finishFeedbackAttachmentUpload(root, uploadGeneration);
	} catch (error) {
		if (!isCurrentFeedbackAttachmentUpload(root, uploadGeneration)) return;
		console.warn('[feedback] file upload failed', error);
		if (preview) {
			preview.style.display = 'none';
			preview.style.opacity = '';
			preview.removeAttribute('data-uploaded-key');
			preview.removeAttribute('data-uploaded-submission-id');
		}
		if (clear) {
			clear.style.display = '';
			clear.setAttribute('aria-label', 'Remove screenshot');
			clear.setAttribute('title', 'Remove screenshot');
		}
		if (zone) zone.style.pointerEvents = '';
		finishFeedbackAttachmentUpload(root, uploadGeneration);
		paintOperationFeedback(root, 'feedbackAttachmentAlert', 'Upload failed. Please try again.');
	}
}
